<script setup lang="ts">
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import UniCredenza from '~kui/components/KUI/Unified/Credenza/UniCredenza.vue';
import UniCredenzaContent from '~kui/components/KUI/Unified/Credenza/UniCredenzaContent.vue';
import UniCredenzaDescription from '~kui/components/KUI/Unified/Credenza/UniCredenzaDescription.vue';
import UniCredenzaTitle from '~kui/components/KUI/Unified/Credenza/UniCredenzaTitle.vue';

interface Props {
  nested?: boolean;
}

withDefaults(defineProps<Props>(), {
  nested: false,
});

const emit = defineEmits<{
  confirm: [];
}>();

const { t } = useI18n();

const open = defineModel<boolean>('open', { required: true });
</script>

<template>
  <UniCredenza
    v-model:open="open"
    :dismissable="true"
    :nested="nested"
  >
    <UniCredenzaContent>
      <div class="flex flex-col items-center gap-4 p-6 pb-8">
        <NuxtImg
          src="/images/learn/melting-ice.png"
          format="webp"
          width="136"
          height="132"
          alt=""
        />
        <!--
          The question and its consequence are what the dialog is called and what it says, so they
          are the credenza's own title and description rather than a heading that happens to sit
          inside one — the primitive points `aria-labelledby`/`aria-describedby` at these, and
          without them the dialog opens unnamed and reka warns on every open. Rendered visibly
          rather than duplicated sr-only: the name a screen reader hears is the copy on screen.
        -->
        <UniCredenzaTitle as-child>
          <Heading
            as="h2"
            size="xl"
            class="text-center"
          >
            {{ t('unified.activity.closeConfirmation.title') }}
          </Heading>
        </UniCredenzaTitle>
        <UniCredenzaDescription as-child>
          <Text class="text-center" size="lg">
            {{ t('unified.activity.closeConfirmation.description') }}
          </Text>
        </UniCredenzaDescription>
        <div class="flex flex-col items-center gap-4 w-full mt-2">
          <Button
            size="lg"
            data-testid="activity-close-confirm"
            type="button"
            class="w-full"
            @click="emit('confirm')"
          >
            {{ t('unified.activity.closeConfirmation.confirm') }}
          </Button>
          <Button
            variant="link"
            size="sm"
            type="button"
            @click="open = false"
          >
            {{ t('unified.activity.closeConfirmation.cancel') }}
          </Button>
        </div>
      </div>
    </UniCredenzaContent>
  </UniCredenza>
</template>
